import { useCallback, useEffect, useState } from 'react';
import {
  fetchUserInterestVector,
  hasInterestVector
} from '../utils/interestTestCompletion';
import {
  CURRENT_INTEREST_BANK_VERSION,
  INTEREST_VECTOR_LENGTH,
  isScoreableInterestVectorLength
} from '../config/productVectors';

/**
 * Load and track the signed-in user's interest_vector for the current bank.
 * @param {{ enabled?: boolean, refreshKey?: number }} options
 */
export function useUserInterestVector({ enabled = true, refreshKey = 0 } = {}) {
  const [interestVector, setInterestVector] = useState([]);
  const [interestBankVersion, setInterestBankVersion] = useState(null);
  const [hasVector, setHasVector] = useState(false);
  const [isStale, setIsStale] = useState(false);
  const [loading, setLoading] = useState(enabled);

  const reload = useCallback(async () => {
    if (!localStorage.getItem('token')) {
      setInterestVector([]);
      setInterestBankVersion(null);
      setHasVector(false);
      setIsStale(false);
      setLoading(false);
      return [];
    }
    setLoading(true);
    try {
      const { interestVector: vector, interestBankVersion: version } = await fetchUserInterestVector();
      setInterestVector(vector);
      setInterestBankVersion(version);
      const valid = hasInterestVector(vector, version);
      setHasVector(valid);
      setIsStale(
        Array.isArray(vector) &&
          isScoreableInterestVectorLength(vector.length) &&
          version !== CURRENT_INTEREST_BANK_VERSION
      );
      return vector;
    } catch {
      setInterestVector([]);
      setInterestBankVersion(null);
      setHasVector(false);
      setIsStale(false);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    reload();
  }, [enabled, refreshKey, reload]);

  return {
    interestVector,
    interestBankVersion,
    hasInterestVector: hasVector,
    isStaleInterestVector: isStale,
    loading,
    reload
  };
}
